import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import service from '../../store/store/baseapiservice';
import { useDrawer } from '../../contexts/DrawerContext';
import DrawerManager from '../../components/drawers/DrawerManager';

type ProfileData = {
  ctzn_id: number;
  ctzn_nm: string;
  ctzn_ph: string;
  addr_tx: string;
  wrd_nu: string;
  eml_tx: string;
  orgn_nm: string;
  lst_lgn_ts: string;
};

type ComplaintData = {
  sno: number;
  tckt_id: number;
  tckt_dt: string;
  tckt_sts_nm: string;
  wrd_id: number;
  tckt_ctgry_nm: string;
  support_fst_nm: string;
  support_user_ph: string;
  svm_nm?: string;
  wrd_nm?: string;
  tckt_sb_ctgry_nm?: string;
  addr_tx?: string;
  lng_dscn_tx?: string;
};

type ServiceRequestData = {
  sno: number;
  tckt_id: number;
  tckt_dt: string;
  tckt_sts_nm: string;
  wrd_id: number;
  tckt_ctgry_nm: string;
  support_fst_nm: string;
  support_user_ph: string;
  svm_nm?: string;
  wrd_nm?: string;
  tckt_sb_ctgry_nm?: string;
  addr_tx?: string;
  lng_dscn_tx?: string;
};

type Tab = {
  id: string;
  labelEn: string;
  labelTe: string;
  count: number | null;
  icon: string;
};

type TicketDetails = {
  tckt_id: number;
  tckt_dt: string;
  wrd_nu?: string;
  wrd_nm?: string;
  svm_nm?: string;
  tckt_sb_ctgry_nm?: string;
  tckt_sts_nm: string;
  addr_tx?: string;
  lng_dscn_tx?: string;
  tckt_ctgry_nm: string;
  attachments?: Array<{
    atcht_url_tx?: string;
    fl_name?: string;
  }>;
  comments?: Array<{
    cmnt_tx: string;
    cmnt_dt: string;
    create_user_nm?: string;
  }>;
};

export default function ProfilePage() {
  const { openDrawer } = useDrawer();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [formData, setFormData] = useState({
    ctzn_nm: '',
    ctzn_ph: '',
    eml_tx: '',
    addr_tx: '',
    orgn_nm: ''
  });
  
  // Complaints state
  const [complaints, setComplaints] = useState<ComplaintData[]>([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [complaintsError, setComplaintsError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Service Requests state
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestData[]>([]);
  const [serviceRequestsLoading, setServiceRequestsLoading] = useState(false);
  const [serviceRequestsError, setServiceRequestsError] = useState<string | null>(null);
  const [currentPageRequests, setCurrentPageRequests] = useState(1);
  const [itemsPerPageRequests, setItemsPerPageRequests] = useState(10);
  
  // Category drawer state (kept local for chat drawer integration)
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [expandedCategoryId, setExpandedCategoryId] = useState<number | null>(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  
  // Service Request drawer state (kept local for selection menu)
  const [isServiceRequestDrawerOpen, setIsServiceRequestDrawerOpen] = useState(false);
  
  // Bulk Garbage Collection form state
  const [bulkGarbageForm, setBulkGarbageForm] = useState({
    wrd_id: '',
    svm_id: '',
    address: '',
    garbageWeight: '',
    amount: 0,
    description: '',
    latitude: null as number | null,
    longitude: null as number | null
  });
  const [bulkGarbageWards, setBulkGarbageWards] = useState<Array<{ wrd_id: number; wrd_nm: string; wrd_nu?: number }>>([]);
  const [bulkGarbageSachivalayams, setBulkGarbageSachivalayams] = useState<Array<{ svm_id: number; svm_nm: string }>>([]);
  const [isSubmittingBulkGarbage, setIsSubmittingBulkGarbage] = useState(false);
  const [locationWard, setLocationWard] = useState<{ wrd_id: number; wrd_nu?: number; wrd_nm: string } | null>(null);
  const [locationSachivalayam, setLocationSachivalayam] = useState<{ svm_id: number; svm_nm: string; svm_apfs_cd?: string } | null>(null);
  const [showWardDropdown, setShowWardDropdown] = useState(true);
  const [showSachivalayamDropdown, setShowSachivalayamDropdown] = useState(true);
  
  // Payment screen state
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [submittedTicketId, setSubmittedTicketId] = useState<number | null>(null);
  
  // Chat drawer state
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{ categoryId: number; categoryName: string; subCategoryId: number; subCategoryName: string } | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{ text: string; timestamp: string; sender: 'bot' | 'user'; images?: string[] }>>([]);
  const [messageInput, setMessageInput] = useState('');
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [showPhotoUploadPrompt, setShowPhotoUploadPrompt] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showConfirmationForm, setShowConfirmationForm] = useState(false);
  const [wards, setWards] = useState<Array<{ wrd_id: number; wrd_nm: string; wrd_nu?: number }>>([]);
  const [sachivalayams, setSachivalayams] = useState<Array<{ svm_id: number; svm_nm: string }>>([]);
  // Keep these as strings because <select> option values are strings in the DOM
  const [selectedWard, setSelectedWard] = useState<string>('');
  const [selectedSachivalayam, setSelectedSachivalayam] = useState<string>('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaId, setCaptchaId] = useState<number | null>(null);
  const [captchaImage, setCaptchaImage] = useState<string>('');
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [userProblem, setUserProblem] = useState('');
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showPhotoShareModal, setShowPhotoShareModal] = useState(false);
  const [photoDescription, setPhotoDescription] = useState('');
  const [photoShareFiles, setPhotoShareFiles] = useState<File[]>([]);
  const [photoSharePreviewUrls, setPhotoSharePreviewUrls] = useState<string[]>([]);
  const [isSubmittingPhotos, setIsSubmittingPhotos] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  
  // Ticket details drawer state
  const [isTicketDrawerOpen, setIsTicketDrawerOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [ticketDetails, setTicketDetails] = useState<TicketDetails | null>(null);
  const [isLoadingTicketDetails, setIsLoadingTicketDetails] = useState(false);
  const [ticketDetailsError, setTicketDetailsError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [newDocument, setNewDocument] = useState<File | null>(null);
  const [newDocumentPreview, setNewDocumentPreview] = useState<string | null>(null);
  const [isUpdatingTicket, setIsUpdatingTicket] = useState(false);

  const handleLanguageToggle = (lang: string) => {
    setCurrentLanguage(lang);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Get ctzn_id from localStorage
        const ctznId = localStorage.getItem('citizenId') || localStorage.getItem('ctzn_id');
        if (!ctznId) {
          setError('Citizen ID not found. Please login again.');
          setIsLoading(false);
          return;
        }

        const response: any = await service.get(`public/web/citizen/profile/${ctznId}`);
        
        if (response && response.data && response.data.length > 0) {
          const data = response.data[0];
          setProfileData(data);
          setFormData({
            ctzn_nm: data.ctzn_nm || '',
            ctzn_ph: data.ctzn_ph || '',
            eml_tx: data.eml_tx || '',
            addr_tx: data.addr_tx || '',
            orgn_nm: data.orgn_nm || ''
          });
        } else {
          setError('Profile data not found');
        }
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError(err.message || 'Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Fetch complaints when complaints tab is active
  useEffect(() => {
    if (activeTab === 'complaints') {
      fetchComplaints();
    }
  }, [activeTab]);

  // Fetch service requests when requests tab is active
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchServiceRequests();
    }
  }, [activeTab]);

  const fetchComplaints = async () => {
    setComplaintsLoading(true);
    setComplaintsError(null);
    try {
      // Fetch citizen complaints from support route
      const response: any = await service.get('/support/citizen/tickets/2');

      // Response may be in different shapes; handle both direct array and nested data
      const payload =
        Array.isArray(response?.data) ? response.data :
        Array.isArray(response?.data?.data) ? response.data.data :
        [];

      if (Array.isArray(payload)) {
        setComplaints(payload);
      } else {
        setComplaints([]);
      }
    } catch (err: any) {
      console.error('Error fetching complaints:', err);
      setComplaintsError(err.message || 'Failed to load complaints');
      setComplaints([]);
    } finally {
      setComplaintsLoading(false);
    }
  };

  const fetchServiceRequests = async () => {
    setServiceRequestsLoading(true);
    setServiceRequestsError(null);
    try {
      // Fetch citizen service requests
      const response: any = await service.get('/public/web/e-requests');

      // Response may be in different shapes; handle both direct array and nested data
      const payload =
        Array.isArray(response?.data) ? response.data :
        Array.isArray(response?.data?.data) ? response.data.data :
        [];

      if (Array.isArray(payload)) {
        setServiceRequests(payload);
      } else {
        setServiceRequests([]);
      }
    } catch (err: any) {
      console.error('Error fetching service requests:', err);
      setServiceRequestsError(err.message || 'Failed to load service requests');
      setServiceRequests([]);
    } finally {
      setServiceRequestsLoading(false);
    }
  };

  const handleCreateComplaint = () => {
    // Open category drawer (kept local for chat drawer integration)
    setIsCategoryDrawerOpen(true);
    if (categories.length === 0 && !isLoadingCategories) {
      fetchCategories();
    }
  };

  const handleCreateServiceRequest = () => {
    // Open service request drawer
    setIsServiceRequestDrawerOpen(true);
  };

  const resetBulkGarbageForm = () => {
    setBulkGarbageForm({
      wrd_id: '',
      svm_id: '',
      address: '',
      garbageWeight: '',
      amount: 0,
      description: '',
      latitude: null,
      longitude: null
    });
    setLocationWard(null);
    setLocationSachivalayam(null);
    setShowWardDropdown(true);
    setShowSachivalayamDropdown(true);
    setBulkGarbageSachivalayams([]);
  };

  const handleVerifyPayment = async () => {
    if (!paymentDetails || !submittedTicketId) return;
    
    setIsVerifyingPayment(true);
    try {
      const reqBody = {
        bnk_upi_trns_ref_nu: paymentDetails.mrcht_trnsn_ref_nu,
        cpay_trnsn_ref_nu: paymentDetails.cpay_trnsn_ref_nu,
        tcketId: submittedTicketId
      };

      const response: any = await service.post('/public/web/verify/payment', reqBody);

      if (response && response.status === 200 && response.data) {
        const finalResults = response.data?.data?.finalResults;
        if (finalResults) {
          setPaymentDetails({
            ...paymentDetails,
            trns_sts: finalResults.trns_sts,
            invce_no: finalResults.invce_id,
            rcpt_url: finalResults.rcpt_url
          });
          
          if (finalResults.trns_sts === 'success') {
            alert(currentLanguage === 'en' 
              ? 'Payment successful!' 
              : 'చెల్లింపు విజయవంతంగా!');
            // Close payment screen and refresh
            setShowPaymentScreen(false);
            resetBulkGarbageForm();
            fetchServiceRequests();
          } else {
            alert(currentLanguage === 'en' 
              ? 'Payment verification completed. Status: ' + finalResults.trns_sts 
              : 'చెల్లింపు ధృవీకరణ పూర్తయింది. స్థితి: ' + finalResults.trns_sts);
          }
        }
      } else {
        throw new Error(response?.message || 'Failed to verify payment');
      }
    } catch (err: any) {
      console.error('Error verifying payment:', err);
      alert(err.message || (currentLanguage === 'en' 
        ? 'Failed to verify payment. Please try again.' 
        : 'చెల్లింపును ధృవీకరించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsVerifyingPayment(false);
    }
  };

  const handleServiceRequestClick = (requestType: string) => {
    // Close the service request drawer
    setIsServiceRequestDrawerOpen(false);
    
    // Open the appropriate drawer using centralized system
    if (requestType === 'BULK GARBAGE COLLECTION') {
      openDrawer('bulk-garbage-collection');
    } else if (requestType === 'SEPTIC TANK CLEANING') {
      openDrawer('septic-tank-cleaning');
    } else if (requestType === 'RTI REQUEST') {
      openDrawer('rti-request');
    } else if (requestType === 'REGISTER AS STREET VENDOR') {
      openDrawer('street-vendor-registration');
    } else {
      console.log('Selected service request:', requestType);
    }
  };

  const fetchWardsForBulkGarbage = async () => {
    try {
      const response = await service.get<Array<{ wrd_id: number; wrd_nm: string; wrd_nu?: number }>>('/open/wards');
      if (response.status === 200 && Array.isArray(response.data)) {
        setBulkGarbageWards(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching wards:', err);
    }
  };

  const fetchSachivalayamsForBulkGarbage = async (wardId: number) => {
    try {
      const response: any = await service.post(`/open/ward/sachivalayams/${wardId}`, {});
      const payload =
        Array.isArray(response?.data) ? response.data :
        Array.isArray(response?.data?.data) ? response.data.data :
        [];
      if (response?.status === 200 && Array.isArray(payload)) {
        setBulkGarbageSachivalayams(payload);
      } else {
        setBulkGarbageSachivalayams([]);
      }
    } catch (err: any) {
      console.error('Error fetching sachivalayams:', err);
      setBulkGarbageSachivalayams([]);
    }
  };

  const handleBulkGarbageWardChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const wardIdStr = e.target.value;
    const wardId = parseInt(wardIdStr, 10);
    setBulkGarbageForm({ ...bulkGarbageForm, wrd_id: wardIdStr, svm_id: '' });
    setBulkGarbageSachivalayams([]);
    setLocationWard(null);
    setShowWardDropdown(true);
    setLocationSachivalayam(null);
    setShowSachivalayamDropdown(true);
    if (!isNaN(wardId)) {
      fetchSachivalayamsForBulkGarbage(wardId);
    }
  };

  const handleBulkGarbageWeightChange = (weight: string) => {
    const amount = parseInt(weight, 10);
    setBulkGarbageForm({ ...bulkGarbageForm, garbageWeight: weight, amount });
  };

  const handleShareLocationBulkGarbage = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setBulkGarbageForm({ ...bulkGarbageForm, latitude: lat, longitude: lng });
          
          try {
            // Call chatbot API to get ward and sachivalayam based on location
            const response: any = await service.get(`/open/chatbot/${lat}/${lng}/sachivalayam`);
            
            if (response && response.data) {
              const locationData = response.data;
              
              // Set ward if available
              if (locationData.wrd && locationData.wrd.length > 0) {
                const ward = locationData.wrd[0];
                setLocationWard({
                  wrd_id: ward.wrd_id,
                  wrd_nu: ward.wrd_nu,
                  wrd_nm: ward.wrd_nm
                });
                setBulkGarbageForm({ ...bulkGarbageForm, wrd_id: String(ward.wrd_id), latitude: lat, longitude: lng });
                setShowWardDropdown(false);
                
                // Fetch sachivalayams for this ward
                await fetchSachivalayamsForBulkGarbage(ward.wrd_id);
              }
              
              // Set sachivalayam if available
              if (locationData.svm && locationData.svm.length > 0) {
                const svm = locationData.svm[0];
                setLocationSachivalayam({
                  svm_id: svm.svm_id,
                  svm_nm: svm.svm_nm,
                  svm_apfs_cd: svm.svm_apfs_cd
                });
                setBulkGarbageForm({ ...bulkGarbageForm, svm_id: String(svm.svm_id), latitude: lat, longitude: lng });
                setShowSachivalayamDropdown(false);
              }
              
              alert(currentLanguage === 'en' 
                ? 'Location shared successfully' 
                : 'స్థానం విజయవంతంగా భాగస్వామ్యం చేయబడింది');
            }
          } catch (err: any) {
            console.error('Error fetching location data:', err);
            alert(currentLanguage === 'en' 
              ? 'Location shared but could not fetch ward/sachivalayam details' 
              : 'స్థానం భాగస్వామ్యం చేయబడింది కానీ వార్డ్/సచివాలయం వివరాలను పొందలేకపోయాము');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(currentLanguage === 'en' 
            ? 'Unable to get your location. Please try again.' 
            : 'మీ స్థానాన్ని పొందలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.');
        }
      );
    } else {
      alert(currentLanguage === 'en' 
        ? 'Geolocation is not supported by your browser.' 
        : 'మీ బ్రౌజర్ జియోలొకేషన్‌ను మద్దతు ఇవ్వదు.');
    }
  };

  const handleSubmitBulkGarbage = async () => {
    // Validation - check both location-based and form values
    const hasWard = locationWard?.wrd_id || bulkGarbageForm.wrd_id;
    const hasSvm = locationSachivalayam?.svm_id || bulkGarbageForm.svm_id;
    
    if (!hasWard) {
      alert(currentLanguage === 'en' ? 'Please select Ward' : 'దయచేసి వార్డ్ ఎంచుకోండి');
      return;
    }
    if (!hasSvm) {
      alert(currentLanguage === 'en' ? 'Please select Sachivalayam' : 'దయచేసి సచివాలయం ఎంచుకోండి');
      return;
    }
    if (!bulkGarbageForm.address.trim()) {
      alert(currentLanguage === 'en' ? 'Please enter address' : 'దయచేసి చిరునామా నమోదు చేయండి');
      return;
    }
    if (!bulkGarbageForm.garbageWeight) {
      alert(currentLanguage === 'en' ? 'Please select garbage weight' : 'దయచేసి చెత్త బరువును ఎంచుకోండి');
      return;
    }
    if (!bulkGarbageForm.description.trim()) {
      alert(currentLanguage === 'en' ? 'Please describe your request' : 'దయచేసి మీ అభ్యర్థనను వివరించండి');
      return;
    }

    setIsSubmittingBulkGarbage(true);
    try {
      // Use location-based values if available, otherwise use selected dropdown values
      const finalWrdId = locationWard?.wrd_id || (bulkGarbageForm.wrd_id ? parseInt(bulkGarbageForm.wrd_id, 10) : 0);
      const finalSvmId = locationSachivalayam?.svm_id || (bulkGarbageForm.svm_id ? parseInt(bulkGarbageForm.svm_id, 10) : 0);

      if (!finalWrdId || finalWrdId === 0) {
        alert(currentLanguage === 'en' ? 'Please select Ward' : 'దయచేసి వార్డ్ ఎంచుకోండి');
        setIsSubmittingBulkGarbage(false);
        return;
      }
      if (!finalSvmId || finalSvmId === 0) {
        alert(currentLanguage === 'en' ? 'Please select Sachivalayam' : 'దయచేసి సచివాలయం ఎంచుకోండి');
        setIsSubmittingBulkGarbage(false);
        return;
      }
    
      // Citizen details from localStorage
      const ctznNm = localStorage.getItem('citizenName') || '';
      const ctznPh = localStorage.getItem('citizenMobile') || '';

      const postData = {
        data: {
          // Will be set by backend based on request type
          wrd_id: finalWrdId,
          svm_id: finalSvmId,
          prty_id: 1,
          Address: bulkGarbageForm.address,
          amnt: bulkGarbageForm.amount,
          lng_dscn_tx: bulkGarbageForm.description,
          tckt_type_id: 3, 
          tckt_ctgry_id: 30,
          // prdct_grp_id: 157,
          tkt_srce_id: 11,
          tckt_stts_id: 17,
          // prdct_grp_nm: "BULK GARBAGE COLLECTION",
          ctzn_nm: ctznNm,
          ctzn_ph: ctznPh,
          eRqustCnt: "BULK GARBAGE COLLECTION",
          trpsCnt: 1,
          clctn_type_id: 10,
          vrynce_type: "30",
          trpsAmnt: 100,
          prdctGrpDtls: {
            prdct_grp_id: 157,
            prdct_grp_nm: "BULK GARBAGE COLLECTION"
          },
        }
      };

console.log(postData);


      const response: any = await service.post('/public/web/e-request/tickets', postData);

      if (response && response.status === 200) {
        // Check if payment details are in the response
        // Response structure: response.data[3].data.data contains payment details
        let paymentData = null;
        let ticketId = null;
        let assignedTo = null;
        
        if (response.data && Array.isArray(response.data)) {
          // Get ticket ID from response.data[2]
          if (response.data.length > 2) {
            ticketId = response.data[2];
          }
          
          // Get assigned user from response.data[1]
          if (response.data.length > 1 && Array.isArray(response.data[1]) && response.data[1].length > 0) {
            assignedTo = response.data[1][0].fst_nm;
          }
          
          // Get payment data from response.data[3]
          if (response.data.length > 3 && response.data[3]?.data?.cpay_status === 'success') {
            paymentData = response.data[3].data.data;
            if (assignedTo) {
              paymentData.asgn_usr_nm = assignedTo;
            }
          }
        }
        
        if (paymentData && paymentData.qr_code) {
          // Show payment screen with QR code
          setPaymentDetails(paymentData);
          setSubmittedTicketId(ticketId);
          setShowPaymentScreen(true);
        } else {
          // No payment required, just show success
          alert(currentLanguage === 'en' 
            ? 'Service request submitted successfully!' 
            : 'సేవా అభ్యర్థన విజయవంతంగా సమర్పించబడింది!');
          resetBulkGarbageForm();
          fetchServiceRequests();
        }
      } else {
        throw new Error(response?.message || 'Failed to submit request');
      }
    } catch (err: any) {
      console.error('Error submitting bulk garbage request:', err);
      alert(err.message || (currentLanguage === 'en' 
        ? 'Failed to submit request. Please try again.' 
        : 'అభ్యర్థనను సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsSubmittingBulkGarbage(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setIsLoadingCategories(true);
      setCategoriesError(null);

      const response: any = await service.get('open/grievances/ticket/categories/sub-categories');
      
      if (response && response.data && Array.isArray(response.data)) {
        setCategories(response.data);
        // Expand first category by default
        if (response.data.length > 0) {
          setExpandedCategoryId(response.data[0].tckt_ctgry_id);
        }
      } else {
        setCategoriesError(currentLanguage === 'en' ? 'Unable to load categories' : 'వర్గాలను లోడ్ చేయలేకపోయాం');
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setCategoriesError(
        err?.message ||
          (currentLanguage === 'en' ? 'Unable to load categories' : 'వర్గాలను లోడ్ చేయలేకపోయాం')
      );
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleCategoryToggle = (categoryId: number) => {
    setExpandedCategoryId(prev => (prev === categoryId ? null : categoryId));
  };

  const handleSubCategoryClick = (category: any, subCategory: any) => {
    // Store selected category and subcategory
    const newSelectedCategory = {
      categoryId: category.tckt_ctgry_id,
      categoryName: category.tckt_ctgry_nm,
      subCategoryId: subCategory.tckt_sb_ctgry_id,
      subCategoryName: subCategory.tckt_sb_ctgry_nm
    };
    setSelectedCategory(newSelectedCategory);

    const grvnceDtls = {
      slctdCtgry: category.tckt_ctgry_id,
      slctdSbCtgry: subCategory.tckt_sb_ctgry_id,
      slctdCtgryNm: category.tckt_ctgry_nm,
      slctdSbCtgryNm: subCategory.tckt_sb_ctgry_nm
    };
    localStorage.setItem('grvnceDtls', JSON.stringify(grvnceDtls));

    // Close category drawer and open RMC Sevak chat drawer via centralized DrawerManager
    setIsCategoryDrawerOpen(false);
    openDrawer('chat-drawer');
  };

  const openChatDrawer = () => {
    setIsChatDrawerOpen(true);

    // Initialize chat messages
    const initialMessages = [
      {
        text: currentLanguage === 'en' 
          ? 'Welcome to RMC Support. Thank you for contacting us for support.' 
          : 'RMC మద్దతుకు స్వాగతం. మద్దతు కోసం మమ్మల్ని సంప్రదించినందుకు ధన్యవాదాలు.',
        timestamp: new Date().toLocaleString('en-IN', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        sender: 'bot' as const
      },
      {
        text: currentLanguage === 'en'
          ? 'Now we will be asking few short questions to process your request.'
          : 'ఇప్పుడు మేము మీ అభ్యర్థనను ప్రాసెస్ చేయడానికి కొన్ని చిన్న ప్రశ్నలు అడుగుతాము.',
        timestamp: new Date().toLocaleString('en-IN', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        sender: 'bot' as const
      },
      {
        text: currentLanguage === 'en'
          ? 'Please describe your problem'
          : 'దయచేసి మీ సమస్యను వివరించండి',
        timestamp: new Date().toLocaleString('en-IN', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        sender: 'bot' as const
      }
    ];
    setChatMessages(initialMessages);
  };

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;

    // Store user problem text
    setUserProblem(messageInput);

    // Add user message
    const userMessage = {
      text: messageInput,
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'user' as const
    };

    setChatMessages(prev => [...prev, userMessage]);
    setMessageInput('');

    // Show location prompt after user sends a message
    setTimeout(() => {
      const botResponse = {
        text: currentLanguage === 'en'
          ? 'Please share the location of the Problem site/location.'
          : 'దయచేసి సమస్య స్థలం/స్థానం యొక్క స్థానాన్ని భాగస్వామ్యం చేయండి.',
        timestamp: new Date().toLocaleString('en-IN', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        }),
        sender: 'bot' as const
      };
      setChatMessages(prev => [...prev, botResponse]);
      setShowLocationPrompt(true);
    }, 1000);
  };

  const handleShareLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setLatitude(lat);
          setLongitude(lng);
          const locationText = `${lat}, ${lng}`;
          setShowLocationPrompt(false);
          
          // Add location message to chat
          const locationMessage = {
            text: currentLanguage === 'en'
              ? `Location shared: ${locationText}`
              : `స్థానం భాగస్వామ్యం చేయబడింది: ${locationText}`,
            timestamp: new Date().toLocaleString('en-IN', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            }),
            sender: 'user' as const
          };
          setChatMessages(prev => [...prev, locationMessage]);
          
          // Show photo upload prompt after location is shared
          setTimeout(() => {
            showPhotoUploadRequest();
          }, 1000);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(currentLanguage === 'en' 
            ? 'Unable to get your location. Please try again.' 
            : 'మీ స్థానాన్ని పొందలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.');
        }
      );
    } else {
      alert(currentLanguage === 'en' 
        ? 'Geolocation is not supported by your browser.' 
        : 'మీ బ్రౌజర్ జియోలొకేషన్‌ను మద్దతు ఇవ్వదు.');
    }
  };

  const showPhotoUploadRequest = () => {
    const photoRequestMessage = {
      text: currentLanguage === 'en'
        ? 'Please share photos related to requests'
        : 'దయచేసి అభ్యర్థనకు సంబంధించిన ఫోటోలను భాగస్వామ్యం చేయండి',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'bot' as const
    };
    setChatMessages(prev => [...prev, photoRequestMessage]);
    setShowPhotoUploadPrompt(true);
  };

  const handleSkipLocation = () => {
    setShowLocationPrompt(false);
    
    // Add skip confirmation message
    const skipMessage = {
      text: currentLanguage === 'en'
        ? 'Location skipped'
        : 'స్థానం దాటవేయబడింది',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'user' as const
    };
    setChatMessages(prev => [...prev, skipMessage]);
    
    // Show photo upload prompt after location is skipped
    setTimeout(() => {
      showPhotoUploadRequest();
    }, 1000);
  };

  const handleSelectPhotos = () => {
    // Open file input for photo selection
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e: any) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files) as File[];
        setSelectedFiles(files);
        
        // Create preview URLs for images
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        const imagePromises = imageFiles.map(file => {
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve(e.target?.result as string);
            };
            reader.readAsDataURL(file);
          });
        });
        
        Promise.all(imagePromises).then((urls) => {
          // Add photos as user message
          const photoMessage = {
            text: currentLanguage === 'en'
              ? `Uploaded ${files.length} photo(s)`
              : `${files.length} ఫోటో(లు) అప్‌లోడ్ చేయబడ్డాయి`,
            timestamp: new Date().toLocaleString('en-IN', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            }),
            sender: 'user' as const,
            images: urls
          };
          setChatMessages(prev => [...prev, photoMessage]);
          setShowPhotoUploadPrompt(false);
          
          // Show confirmation form after photos are uploaded
          setTimeout(() => {
            showConfirmationRequest();
          }, 1000);
        });
      }
    };
    input.click();
  };

  const handleSkipUpload = () => {
    setShowPhotoUploadPrompt(false);
    
    // Add skip confirmation message
    const skipMessage = {
      text: currentLanguage === 'en'
        ? 'Upload skipped'
        : 'అప్‌లోడ్ దాటవేయబడింది',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'user' as const
    };
    setChatMessages(prev => [...prev, skipMessage]);
    
    // Show confirmation form after upload is skipped
    setTimeout(() => {
      showConfirmationRequest();
    }, 1000);
  };

  const showConfirmationRequest = async () => {
    // Fetch wards
    await fetchWards();
    
    // Generate captcha
    generateCaptcha();
    
    // Add bot message asking for confirmation
    const confirmationMessage = {
      text: currentLanguage === 'en'
        ? 'Please confirm your details'
        : 'దయచేసి మీ వివరాలను నిర్ధారించండి',
      timestamp: new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      sender: 'bot' as const
    };
    setChatMessages(prev => [...prev, confirmationMessage]);
    setShowConfirmationForm(true);
  };

  const fetchWards = async () => {
    try {
      const response = await service.get<Array<{ wrd_id: number; wrd_nm: string; wrd_nu?: number }>>('/open/wards');
      if (response.status === 200 && Array.isArray(response.data)) {
        setWards(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching wards:', err);
    }
  };

  const fetchSachivalayams = async (wardId: number) => {
    try {
      const response: any = await service.post(`/open/ward/sachivalayams/${wardId}`, {});
        
      // Some APIs return payload as `data`, others as `{ data: [...] }`
      const payload =
        Array.isArray(response?.data) ? response.data :
        Array.isArray(response?.data?.data) ? response.data.data :
        [];

      if (response?.status === 200 && Array.isArray(payload)) {
        setSachivalayams(payload);
        console.log(payload,'payload');
        // Keep the user's current selection if it still exists in the new list
        if (selectedSachivalayam && !payload.some((svm) => String(svm.svm_id) === selectedSachivalayam)) {
          setSelectedSachivalayam('');
        }
      } else {
        setSachivalayams([]);
        setSelectedSachivalayam('');
      }
    } catch (err: any) {
      console.error('Error fetching sachivalayams:', err);
      setSachivalayams([]);
      setSelectedSachivalayam('');
    }
  };

  const generateCaptcha = async () => {
    setIsLoadingCaptcha(true);
    try {
      const response: any = await service.get('/auth2/ss/login/capcha');
      
      if (response && response.status === 200 && response.data) {
        setCaptchaId(response.data.cptch_id);
        setCaptchaImage(response.data.cptch_img_src_tx);
        // Clear previous input
        setCaptchaInput('');
      } else {
        console.error('Failed to fetch captcha:', response?.message);
        alert(currentLanguage === 'en' 
          ? 'Failed to load captcha. Please try again.' 
          : 'క్యాప్చాను లోడ్ చేయడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.');
      }
    } catch (err: any) {
      console.error('Error fetching captcha:', err);
      alert(currentLanguage === 'en' 
        ? 'Error loading captcha. Please try again.' 
        : 'క్యాప్చాను లోడ్ చేయడంలో లోపం. దయచేసి మళ్లీ ప్రయత్నించండి.');
    } finally {
      setIsLoadingCaptcha(false);
    }
  };

  const handleWardChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const wardIdStr = e.target.value;
    const wardId = parseInt(wardIdStr, 10);
    const normalizedWardId = Number.isNaN(wardId) ? null : wardId;

    setSelectedWard(wardIdStr);
    // Reset sachivalayam whenever ward changes to avoid binding stale value
    setSelectedSachivalayam('');

    if (normalizedWardId) {
      fetchSachivalayams(normalizedWardId);
    } else {
      setSachivalayams([]);
    }
  };

  const handleSachivalayamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSachivalayam(e.target.value);
  };

  const handleConfirmYes = async () => {
    if (!captchaInput || !captchaInput.trim()) {
      alert(currentLanguage === 'en' 
        ? 'Please enter captcha' 
        : 'దయచేసి క్యాప్చాను నమోదు చేయండి');
      return;
    }

    if (!captchaId) {
      alert(currentLanguage === 'en' 
        ? 'Captcha not loaded. Please refresh and try again.' 
        : 'క్యాప్చా లోడ్ కాలేదు. దయచేసి రిఫ్రెష్ చేసి మళ్లీ ప్రయత్నించండి.');
      generateCaptcha();
      return;
    }

    // Validate ward and sachivalayam
    if (!selectedWard) {
      alert(currentLanguage === 'en'
        ? 'Please select Ward.'
        : 'దయచేసి వార్డ్ ఎంచుకోండి');
      return;
    }
    if (!selectedSachivalayam) {
      alert(currentLanguage === 'en'
        ? 'Please select Sachivalayam.'
        : 'దయచేసి సచివాలయం ఎంచుకోండి');
      return;
    }

    setIsSubmittingTicket(true);

    try {
      // Convert selectedFiles to base64 attachments - MUST match controller validation schema
      // Controller expects: base64, fl_name, fl_type, cht_dt, cht_tm, lat (optional), lng (optional)
      const attachments: Array<{ 
        base64: string; 
        fl_name: string; 
        fl_type: string; 
        cht_dt: string; 
        cht_tm: string; 
        lat?: number; 
        lng?: number; 
      }> = [];
      
      if (selectedFiles.length > 0) {
        // Format date and time as required by controller
        const currentDate = new Date();
        // Date format: YYYY-MM-DD (matches backend std.STD_DATE_FORMAT)
        const day = String(currentDate.getDate()).padStart(2, '0');
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const year = currentDate.getFullYear();
        const cht_dt = `${year}-${month}-${day}`;
        
        // Time format: hh:mm a (e.g., "05:27 pm")
        const hours = currentDate.getHours();
        const minutes = String(currentDate.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';
        const hours12 = hours % 12 || 12;
        const cht_tm = `${String(hours12).padStart(2, '0')}:${minutes} ${ampm}`;
        
        const attachmentPromises = selectedFiles.map(file => {
          return new Promise<{ 
            base64: string; 
            fl_name: string; 
            fl_type: string; 
            cht_dt: string; 
            cht_tm: string; 
            lat?: number; 
            lng?: number; 
          }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              // Angular uses reader.result directly (full data URL: data:image/jpeg;base64,/9j/4AAQ...)
              // The controller validation expects the full data URL format, not just the base64 string
              const base64 = e.target?.result as string;
              
              // Build attachment object with EXACT field names expected by controller
              // DO NOT use: name, data, type (these will cause validation errors)
              const attachment: {
                base64: string;
                fl_name: string;
                fl_type: string;
                cht_dt: string;
                cht_tm: string;
                lat?: number;
                lng?: number;
              } = {
                base64: base64,        // Full data URL (data:image/jpeg;base64,...) - matching Angular
                fl_name: file.name,    // NOT 'name'
                fl_type: file.type,    // NOT 'type'
                cht_dt: cht_dt,
                cht_tm: cht_tm
              };
              
              // Only add lat/lng if they have valid values
              if (latitude !== null && latitude !== undefined && latitude !== 0) {
                attachment.lat = latitude;
              }
              if (longitude !== null && longitude !== undefined && longitude !== 0) {
                attachment.lng = longitude;
              }
              
              resolve(attachment);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        });

        const attchmnts = await Promise.all(attachmentPromises);
        attachments.push(...attchmnts);
      }

      // Debug: Log selected sachivalayam value before parsing
      console.log('=== SACHIVALAYAM DEBUG ===');
      console.log('selectedSachivalayam (raw):', selectedSachivalayam);
      console.log('selectedSachivalayam type:', typeof selectedSachivalayam);
      console.log('sachivalayams list:', sachivalayams);
      const selectedSvmObject = sachivalayams.find(svm => String(svm.svm_id) === selectedSachivalayam);
      console.log('selectedSvmObject:', selectedSvmObject);
      const parsedSvmId = parseInt(selectedSachivalayam, 10);
      console.log('parsedSvmId:', parsedSvmId);
      console.log('isNaN check:', isNaN(parsedSvmId));
      console.log('========================');

      // Ensure we have a valid svm_id
      const finalSvmId = selectedSvmObject?.svm_id || parsedSvmId || 0;
      
      if (!finalSvmId || finalSvmId === 0) {
        alert(currentLanguage === 'en'
          ? 'Invalid Sachivalayam selected. Please select again.'
          : 'అనైచ్ఛిక సచివాలయం ఎంచుకోబడింది. దయచేసి మళ్లీ ఎంచుకోండి.');
        setIsSubmittingTicket(false);
        return;
      }

      // Prepare ticket data - matching Angular structure exactly
      const ticketData = {
        atchmnts: attachments,
        cmnt: userProblem || '',
        ctgry_id: selectedCategory?.categoryId || 0,
        priority: 3,
        status: 5,
        lat: latitude || 0,
        lng: longitude || 0,
        subcat: selectedCategory?.subCategoryId || 0,
        wrdnum: parseInt(selectedWard, 10) || 0,
        // IMPORTANT: pass selected svm_id as svmid - use finalSvmId which is guaranteed to be correct
        svmid: finalSvmId,
        captchaID: captchaId,
        captcha: captchaInput.trim()
      };

      // Wrap in data object as expected by controller (matching Angular apiSrv.create structure)
      const postData = {
        data: ticketData
      };
      
      // Debug log before sending
      console.log('=== TICKET SUBMISSION DEBUG ===');
      console.log('PostData structure:', JSON.stringify(postData, null, 2));
      console.log('Ticket data:', JSON.stringify(ticketData, null, 2));
      console.log('Final svmid being sent:', ticketData.svmid);
      console.log('==============================');

      // Debug log before sending
      console.log('=== TICKET SUBMISSION DEBUG ===');
      console.log('PostData structure:', JSON.stringify(postData, null, 2));
      console.log('Ticket data:', JSON.stringify(ticketData, null, 2));
      console.log('==============================');

      // Submit ticket - baseapiservice already handles token from localStorage
      const response: any = await service.post('/support/chatbot/tickets', postData);

      if (response && response.status === 200 && response.data) {
        const ticketData = response.data;
        const ticketNumber = ticketData.tckt_num || ticketData.tckt_id || 'N/A';
        const assignedUser = ticketData.asgn_usrid && ticketData.asgn_usrid.length > 0 
          ? ticketData.asgn_usrid[0].fst_nm 
          : '';
        const assignedUserMobile = ticketData.asgn_usrid && ticketData.asgn_usrid.length > 0 
          ? ticketData.asgn_usrid[0].mble_ph 
          : '';

        // Add success message to chat
        let successMessage = '';
        if (assignedUser) {
          successMessage = currentLanguage === 'en'
            ? `Your ticket is assigned to ${assignedUser}${assignedUserMobile ? ` -(${assignedUserMobile})` : ''} and for further help, Please find the Ticket reference No. ${ticketNumber}, Our support team will reach you soon. You can find these tickets under View Tickets`
            : `మీ టికెట్ ${assignedUser}${assignedUserMobile ? ` -(${assignedUserMobile})` : ''}కి కేటాయించబడింది మరియు మరింత సహాయం కోసం, దయచేసి టికెట్ రిఫరెన్స్ నంబర్ ${ticketNumber}ని కనుగొనండి, మా మద్దతు బృందం త్వరలో మీకు చేరుకుంటుంది. మీరు ఈ టికెట్లను వ్యూ టికెట్స్ కింద కనుగొనవచ్చు`;
        } else {
          successMessage = currentLanguage === 'en'
            ? `For further help, Please find the Ticket reference No. ${ticketNumber}, Our support team will reach you soon. You can find these tickets under View Tickets`
            : `మరింత సహాయం కోసం, దయచేసి టికెట్ రిఫరెన్స్ నంబర్ ${ticketNumber}ని కనుగొనండి, మా మద్దతు బృందం త్వరలో మీకు చేరుకుంటుంది. మీరు ఈ టికెట్లను వ్యూ టికెట్స్ కింద కనుగొనవచ్చు`;
        }

        const botSuccessMessage = {
          text: successMessage,
          timestamp: new Date().toLocaleString('en-IN', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sender: 'bot' as const
        };
        setChatMessages(prev => [...prev, botSuccessMessage]);

        // Close confirmation form
        setShowConfirmationForm(false);

        // Show success popup
        alert(currentLanguage === 'en'
          ? `Ticket created successfully! Ticket No: ${ticketNumber}`
          : `టికెట్ విజయవంతంగా సృష్టించబడింది! టికెట్ నంబర్: ${ticketNumber}`);

        // Optionally close chat drawer after a delay
        setTimeout(() => {
          closeChatDrawer();
        }, 3000);
      } else {
        throw new Error(response?.message || 'Failed to submit ticket');
      }
    } catch (err: any) {
      console.error('Error submitting ticket:', err);
      
      // Reset captcha on error
      await generateCaptcha();
      setCaptchaInput('');
      
      alert(err?.message || (currentLanguage === 'en' 
        ? 'Failed to submit ticket. Please try again.' 
        : 'టికెట్ సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const handleConfirmNo = () => {
    setShowConfirmationForm(false);
    setSelectedWard('');
    setSelectedSachivalayam('');
    setCaptchaInput('');
    generateCaptcha();
  };

  const handleCloseClick = () => {
    setShowExitConfirmation(true);
  };

  const closeChatDrawer = () => {
    setIsChatDrawerOpen(false);
    setSelectedCategory(null);
    setChatMessages([]);
    setMessageInput('');
    setShowLocationPrompt(false);
    setShowPhotoUploadPrompt(false);
    setSelectedFiles([]);
    setShowConfirmationForm(false);
    setSelectedWard('');
    setSelectedSachivalayam('');
    setCaptchaInput('');
    setUserProblem('');
    setShowExitConfirmation(false);
    setShowPhotoShareModal(false);
    setPhotoShareFiles([]);
    setPhotoSharePreviewUrls([]);
    setPhotoDescription('');
  };

  const handleExitYes = () => {
    closeChatDrawer();
  };

  const handleExitNo = () => {
    setShowExitConfirmation(false);
  };

  const getUserName = () => {
    return localStorage.getItem('citizenName') || 'User';
  };

  const handlePhotoShareFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setPhotoShareFiles(files);
      
      // Create preview URLs for images
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      const imagePromises = imageFiles.map(file => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result as string);
          };
          reader.readAsDataURL(file);
        });
      });
      
      Promise.all(imagePromises).then((urls) => {
        setPhotoSharePreviewUrls(urls);
      });
    }
  };

  const handleSubmitPhotos = async () => {
    if (photoShareFiles.length === 0) {
      alert(currentLanguage === 'en' 
        ? 'Please upload at least one photo' 
        : 'దయచేసి కనీసం ఒక ఫోటోను అప్‌లోడ్ చేయండి');
      return;
    }
    
    if (!photoDescription.trim()) {
      alert(currentLanguage === 'en' 
        ? 'Please describe your photo' 
        : 'దయచేసి మీ ఫోటోను వివరించండి');
      return;
    }

    setIsSubmittingPhotos(true);

    try {
      // Get citizen ID from localStorage
      const citizenId = localStorage.getItem('citizenId') || sessionStorage.getItem('citizenId');
      const citizenName = localStorage.getItem('citizenName') || 'Anonymous';

      // Convert files to base64 for attachments
      const attachmentPromises = photoShareFiles.map(file => {
        return new Promise<{ name: string; data: string; type: string }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64 = (e.target?.result as string).split(',')[1];
            resolve({
              name: file.name,
              data: base64,
              type: file.type
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const attachments = await Promise.all(attachmentPromises);

      // Photo share data structure matching the API requirements
      const photoshareData = {
        name: citizenName,
        ctzn_id: citizenId ? parseInt(citizenId) : 0,
        prty_id: 1,
        lng_dscn_tx: photoDescription,
        atchmnts: attachments,
        tckt_type_id: 4,
        tckt_ctgry_id: 35,
        sprt_usr_id: 1581,
        sprt_tm_id: 875,
        tckt_sb_ctgry_id: 0,
        tckt_stts_id: 13,
        tkt_srce_id: 11,
        chnge_tx: '',
        ctzn_in: 1
      };

      const token = localStorage.getItem('token') || 
                   localStorage.getItem('x-access-token') || 
                   sessionStorage.getItem('token') || 
                   sessionStorage.getItem('x-access-token');

      const headers: HeadersInit = {};
      if (token) {
        headers['x-access-token'] = token;
      }

      const response = await service.post('/public/web/city-associate/photo-share/tickets', photoshareData, {
        headers
      });

      if (response.status === 200) {
        // Add success message to chat
        const fileNames = photoShareFiles.map(f => f.name).join(', ');
        const successMessage = {
          text: currentLanguage === 'en'
            ? `Uploaded ${photoShareFiles.length} file(s): ${fileNames}`
            : `${photoShareFiles.length} ఫైల్(లు) అప్‌లోడ్ చేయబడ్డాయి: ${fileNames}`,
          timestamp: new Date().toLocaleString('en-IN', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          sender: 'user' as const,
          images: photoSharePreviewUrls.length > 0 ? photoSharePreviewUrls : undefined
        };
        setChatMessages(prev => [...prev, successMessage]);
        
        // Close modal and reset
        setShowPhotoShareModal(false);
        setPhotoShareFiles([]);
        setPhotoSharePreviewUrls([]);
        setPhotoDescription('');
        
        // Show confirmation form after photos are submitted
        setTimeout(() => {
          showConfirmationRequest();
        }, 1000);
      } else {
        alert(currentLanguage === 'en' 
          ? 'Failed to submit photos. Please try again.' 
          : 'ఫోటోలను సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.');
      }
    } catch (error: any) {
      console.error('Error submitting photos:', error);
      alert(currentLanguage === 'en' 
        ? 'Error submitting photos. Please try again.' 
        : 'ఫోటోలను సమర్పించడంలో లోపం. దయచేసి మళ్లీ ప్రయత్నించండి.');
    } finally {
      setIsSubmittingPhotos(false);
    }
  };

  const handleCancelPhotoShare = () => {
    setShowPhotoShareModal(false);
    setPhotoShareFiles([]);
    setPhotoSharePreviewUrls([]);
    setPhotoDescription('');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      setPhotoShareFiles(files);
      
      // Create preview URLs for images
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      const imagePromises = imageFiles.map(file => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result as string);
          };
          reader.readAsDataURL(file);
        });
      });
      
      Promise.all(imagePromises).then((urls) => {
        setPhotoSharePreviewUrls(urls);
      });
    }
  };

  // Use the same icon mapping as RMCSevak to keep category images consistent
  const getCategoryIcon = (categoryName: string, iconPath?: string) => {
    if (iconPath && iconPath.startsWith('http')) return iconPath;
    
    const categoryIconMap: { [key: string]: string } = {
      'BUILDING PLAN PERMISSION': '/assets/images/categories/townplan.png',
      'GARBAGE': '/assets/images/categories/grbgeclct.png',
      'GENERAL': '/assets/images/categories/general.png',
      'GOVERNMENT SCHEMES': '/assets/images/categories/voter.png',
      'PUBLIC HEALTH': '/assets/images/categories/public.png',
      'ROADS AND FOOTPATHS': '/assets/images/categories/Pot_holes.png',
      'STREET LIGHTS': '/assets/images/categories/streetlight.png',
      'TAXES AND LICENCES': '/assets/images/categories/tax.png',
      'WATER STAGNATION': '/assets/images/categories/drainage.png',
      'WATER SUPPLY': '/assets/images/categories/water-tap.png',
      'AIR POLLUTION': '/assets/images/categories/public.png',
      'BIN POINTS': '/assets/images/categories/grbgeclct.png',
      'PARKS': '/assets/images/categories/park.png',
    };

    const upperName = categoryName.toUpperCase();
    if (categoryIconMap[upperName]) {
      return categoryIconMap[upperName];
    }

    for (const [key, value] of Object.entries(categoryIconMap)) {
      if (upperName.includes(key) || key.includes(upperName)) {
        return value;
      }
    }

    if (iconPath) {
      if (iconPath.startsWith('http') || iconPath.startsWith('/')) return iconPath;
      return `/assets/images/categories/${iconPath}`;
    }

    return '/assets/images/categories/general.png';
  };

  const handleViewComplaint = async (complaintOrId: ComplaintData | ServiceRequestData | number) => {
    // Handle both ComplaintData/ServiceRequestData object and ticket ID number
    let complaint: ComplaintData | ServiceRequestData | null = null;
    let ticketId: number;
    let isServiceRequest = false;
    
    if (typeof complaintOrId === 'number') {
      ticketId = complaintOrId;
      // Check if it's a service request first
      const serviceRequest = serviceRequests.find(r => r.tckt_id === ticketId);
      if (serviceRequest) {
        complaint = serviceRequest;
        isServiceRequest = true;
      } else {
        // Find complaint from the complaints array
        complaint = complaints.find(c => c.tckt_id === ticketId) || null;
      }
    } else {
      complaint = complaintOrId;
      ticketId = complaint.tckt_id;
      // Check if it's a service request
      isServiceRequest = serviceRequests.some(r => r.tckt_id === ticketId);
    }
    
    setSelectedTicketId(ticketId);
    setIsTicketDrawerOpen(true);
    setTicketDetailsError(null);
    
    // Immediately populate with table data if available
    const initialTicketData: TicketDetails = complaint ? {
      tckt_id: complaint.tckt_id,
      tckt_dt: complaint.tckt_dt || '-',
      tckt_sts_nm: complaint.tckt_sts_nm || '-',
      tckt_ctgry_nm: complaint.tckt_ctgry_nm || '-',
      // Extract ward number from wrd_nm if it contains a number
      wrd_nu: complaint.wrd_nm ? (complaint.wrd_nm.match(/\d+/) ? complaint.wrd_nm.match(/\d+/)?.[0] : undefined) : undefined,
      // Use wrd_nm directly
      wrd_nm: complaint.wrd_nm || undefined,
      svm_nm: complaint.svm_nm || undefined,
      tckt_sb_ctgry_nm: complaint.tckt_sb_ctgry_nm || '-',
      addr_tx: complaint.addr_tx || '-',
      lng_dscn_tx: complaint.lng_dscn_tx || '-',
      attachments: [],
      comments: []
    } : {
      tckt_id: ticketId,
      tckt_dt: '-',
      tckt_sts_nm: '-',
      tckt_ctgry_nm: '-',
      svm_nm: undefined,
      tckt_sb_ctgry_nm: '-',
      addr_tx: '-',
      lng_dscn_tx: '-',
      attachments: [],
      comments: []
    };
    
    setTicketDetails(initialTicketData);
    setIsLoadingTicketDetails(true);
    
    try {
      // Use different API route for service requests
      // For service requests (type 3), use the route: /support/grievances/ticket/categories/3
      let ticketResponse: any = null;
      
      if (isServiceRequest) {
        // Use the route format specified: /support/grievances/ticket/categories/3 with ticket ID
        // Try different formats to match the backend route
        try {
          // First try: /support/grievances/tickets/:tckt_id/types/3 (matches route pattern)
          ticketResponse = await service.get(`/support/grievances/tickets/types/3`);
        } catch (err) {
          // Fallback: try the exact route user specified with ticket ID as query param
          try {
            ticketResponse = await service.get(`/support/grievances/ticket/categories/3?tckt_id=${ticketId}`);
          } catch (err2) {
            console.error('Error fetching service request details:', err2);
            ticketResponse = null;
          }
        }
      } else {
        // For regular complaints, use the standard route
        ticketResponse = await service.get(`/support/grievances/tickets/${ticketId}/documents`).catch(() => null);
      }
      
      // Fetch comments and documents in parallel
      const [commentsResponse, documentsResponse] = await Promise.all([
        service.get(`/support/getTicketComments/${ticketId}/more`).catch(() => null),
        service.get(`/support/grievances/tickets/${ticketId}/documents`).catch(() => null)
      ]);
      
      // Merge API data with table data
      let ticketData: TicketDetails = { ...initialTicketData };
      
      // Update with full ticket details if available
      if (ticketResponse?.status === 200 && ticketResponse.data) {
        const apiTicketData = Array.isArray(ticketResponse.data) ? ticketResponse.data[0] : ticketResponse.data;
        
        // Extract ward number and name from API response - use wrd_nm directly
        const apiWrdNu = apiTicketData.wrd_nu || (apiTicketData.wrd_nm ? apiTicketData.wrd_nm.match(/\d+/)?.join('') : undefined);
        const apiWrdNm = apiTicketData.wrd_nm || undefined;
        const apiSvmNm = apiTicketData.svm_nm || undefined;
        
        ticketData = {
          ...ticketData,
          ...apiTicketData,
          // Preserve table data if API doesn't have it, but prioritize API data
          tckt_id: apiTicketData.tckt_id || ticketData.tckt_id,
          tckt_dt: apiTicketData.tckt_dt || ticketData.tckt_dt,
          tckt_sts_nm: apiTicketData.tckt_sts_nm || ticketData.tckt_sts_nm,
          tckt_ctgry_nm: apiTicketData.tckt_ctgry_nm || ticketData.tckt_ctgry_nm,
          wrd_nu: apiWrdNu || ticketData.wrd_nu,
          wrd_nm: apiWrdNm || ticketData.wrd_nm,
          svm_nm: apiSvmNm || ticketData.svm_nm,
          tckt_sb_ctgry_nm: apiTicketData.tckt_sb_ctgry_nm || ticketData.tckt_sb_ctgry_nm,
          addr_tx: apiTicketData.addr_tx || apiTicketData.Address || ticketData.addr_tx,
          lng_dscn_tx: apiTicketData.lng_dscn_tx || apiTicketData.description || ticketData.lng_dscn_tx,
          attachments: apiTicketData.attachments || ticketData.attachments,
          comments: apiTicketData.comments || ticketData.comments
        };
      }
      
      // Add comments if available
      if (commentsResponse?.status === 200 && commentsResponse.data && Array.isArray(commentsResponse.data)) {
        ticketData.comments = commentsResponse.data;
      }
      
      // Add documents/attachments if available
      if (documentsResponse?.status === 200 && documentsResponse.data) {
        ticketData.attachments = Array.isArray(documentsResponse.data) ? documentsResponse.data : [];
      }
      
      setTicketDetails(ticketData);
      console.log(ticketData,'ticketData');
    } catch (err: any) {
      console.error('Error fetching ticket details:', err);
      setTicketDetailsError(err.message || 'Failed to load additional ticket details');
      // Keep the table data even if API fails
    } finally {
      setIsLoadingTicketDetails(false);
    }
  };

  const handleCloseTicketDrawer = () => {
    setIsTicketDrawerOpen(false);
    setSelectedTicketId(null);
    setTicketDetails(null);
    setNewComment('');
    setNewDocument(null);
    setNewDocumentPreview(null);
    setTicketDetailsError(null);
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewDocument(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewDocumentPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicketId || (!newComment.trim() && !newDocument)) {
      alert(currentLanguage === 'en' 
        ? 'Please add a comment or upload a document' 
        : 'దయచేసి కామెంట్ జోడించండి లేదా డాక్యుమెంట్ అప్‌లోడ్ చేయండి');
      return;
    }

    setIsUpdatingTicket(true);
    try {
      const attachments: any[] = [];
      
      // Convert document to base64 if provided
      if (newDocument) {
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = (e) => {
            const base64 = e.target?.result as string;
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(newDocument);
        });

        const base64 = await base64Promise;
        // NOTE: inscommentCtrl allows only:
        // atchmnts.+.base64, atchmnts.+.fl_name, atchmnts.+.fl_type, atchmnts.+.lat, atchmnts.+.lng
        const atch: any = {
          base64,
          fl_name: newDocument.name,
          fl_type: newDocument.type
        };
        // Optionally include lat/lng if available (controller accepts these)
        if (bulkGarbageForm.latitude != null) atch.lat = bulkGarbageForm.latitude;
        if (bulkGarbageForm.longitude != null) atch.lng = bulkGarbageForm.longitude;
        attachments.push(atch);
      }

      // Use PUT endpoint to add comment
      // Allowed fields: tckt_id, cmnt_tx, lat, lng, atchmnts (and nested attachment fields)
      const requestBody: any = {
        tckt_id: selectedTicketId,
        cmnt_tx: newComment.trim() || null
      };

      // Only include attachments if there are any
      if (attachments.length > 0) {
        requestBody.atchmnts = attachments;
      }

      const postData = {
        data: requestBody
      };

      const response: any = await service.put(`/support/grievances/tickets/${selectedTicketId}/comment`, postData);

      if (response?.status === 200) {
        // Refresh ticket details
        await handleViewComplaint(selectedTicketId);
        setNewComment('');
        setNewDocument(null);
        setNewDocumentPreview(null);
        alert(currentLanguage === 'en' ? 'Ticket updated successfully' : 'టికెట్ విజయవంతంగా నవీకరించబడింది');
      } else {
        alert(currentLanguage === 'en' ? 'Failed to update ticket' : 'టికెట్ నవీకరించడంలో విఫలమైంది');
      }
    } catch (err: any) {
      console.error('Error updating ticket:', err);
      alert(err.message || (currentLanguage === 'en' ? 'Failed to update ticket' : 'టికెట్ నవీకరించడంలో విఫలమైంది'));
    } finally {
      setIsUpdatingTicket(false);
    }
  };

  // Pagination calculations for complaints
  const totalPages = Math.ceil(complaints.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedComplaints = complaints.slice(startIndex, endIndex);

  // Pagination calculations for service requests
  const totalPagesRequests = Math.ceil(serviceRequests.length / itemsPerPageRequests);
  const startIndexRequests = (currentPageRequests - 1) * itemsPerPageRequests;
  const endIndexRequests = startIndexRequests + itemsPerPageRequests;
  const paginatedServiceRequests = serviceRequests.slice(startIndexRequests, endIndexRequests);

  const handleSave = async () => {
    try {
      const ctznId = localStorage.getItem('citizenId') || localStorage.getItem('ctzn_id');
      if (!ctznId) {
        setError('Citizen ID not found');
        return;
      }

      const response: any = await service.put('public/web/citizen/profile', {
        ctzn_nm: formData.ctzn_nm,
        ctzn_ph: formData.ctzn_ph,
        eml_tx: formData.eml_tx,
        addr_tx: formData.addr_tx,
        orgn_nm: formData.orgn_nm
      });

      if (response && response.data) {
        setIsEditing(false);
        // Refresh profile data
        const profileResponse: any = await service.get(`public/web/citizen/profile/${ctznId}`);
        if (profileResponse && profileResponse.data && profileResponse.data.length > 0) {
          setProfileData(profileResponse.data[0]);
        }
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    }
  };

  const tabs: Tab[] = [
    { id: 'profile', labelEn: 'Your Profile', labelTe: 'మీ ప్రొఫైల్', count: null, icon: 'ri-user-line' },
    { id: 'complaints', labelEn: 'My Complaints', labelTe: 'నా ఫిర్యాదులు', count: complaints.length, icon: 'ri-feedback-line' },
    { id: 'requests', labelEn: 'My Service Requests', labelTe: 'నా సేవా అభ్యర్థనలు', count: serviceRequests.length, icon: 'ri-customer-service-2-line' },
    // 'files' (My e-Files) tab hidden
    { id: 'bookings', labelEn: 'My Bookings', labelTe: 'నా బుకింగ్‌లు', count: 0, icon: 'ri-calendar-check-line' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onLanguageToggle={handleLanguageToggle} currentLanguage={currentLanguage} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg font-medium">Loading profile...</p>
          </div>
        </div>
        <Footer currentLanguage={currentLanguage} />
      </div>
    );
  }

  if (error && !profileData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header onLanguageToggle={handleLanguageToggle} currentLanguage={currentLanguage} />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl border border-red-100 p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
              <i className="ri-error-warning-line text-4xl text-red-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Profile</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <i className="ri-refresh-line mr-2"></i>
              Try Again
            </button>
          </div>
        </div>
        <Footer currentLanguage={currentLanguage} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <Header onLanguageToggle={handleLanguageToggle} currentLanguage={currentLanguage} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 rounded-2xl p-6 mb-8 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <i className="ri-user-3-line text-3xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {currentLanguage === 'en' ? 'My Account' : 'నా ఖాతా'}
                </h1>
                <p className="text-orange-100 text-sm mt-1">
                  {currentLanguage === 'en' ? 'Manage your profile, complaints and service requests' : 'మీ ప్రొఫైల్, ఫిర్యాదులు మరియు సేవా అభ్యర్థనలను నిర్వహించండి'}
                </p>
              </div>
            </div>
            {profileData && (
              <div className="hidden md:flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <span className="text-orange-600 font-bold text-lg">{profileData.ctzn_nm?.charAt(0).toUpperCase() || 'U'}</span>
                </div>
                <div className="text-white">
                  <p className="font-semibold text-sm capitalize">{profileData.ctzn_nm || 'User'}</p>
                  <p className="text-xs text-orange-100">{profileData.ctzn_ph || ''}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex w-full divide-x divide-gray-200 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex-1 min-w-[130px] sm:min-w-0 flex items-center justify-center gap-2 px-4 py-3.5 font-medium text-sm whitespace-nowrap transition-colors duration-300 ${
                  activeTab === tab.id
                    ? 'text-white'
                    : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  <i className={`text-lg ${tab.icon}`}></i>
                  {currentLanguage === 'en' ? tab.labelEn : tab.labelTe}
                  {tab.count !== null && tab.count > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      activeTab === tab.id ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Profile Content */}
        {activeTab === 'profile' && profileData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 relative">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                <div className="relative">
                  <div className="w-28 h-28 rounded-full bg-white/20 backdrop-blur-sm p-1 shadow-2xl">
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-orange-400 via-amber-500 to-orange-600 flex items-center justify-center">
                      {profileData.ctzn_nm ? (
                        <span className="text-white text-4xl font-bold">
                          {profileData.ctzn_nm.charAt(0).toUpperCase()}
                        </span>
                      ) : (
                        <i className="ri-user-line text-white text-4xl"></i>
                      )}
                    </div>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-center md:text-left flex-1">
                  <h2 className="text-2xl font-bold text-white mb-1 capitalize">{profileData.ctzn_nm || 'User'}</h2>
                  <p className="text-blue-100 flex items-center justify-center md:justify-start gap-2">
                    <i className="ri-phone-line"></i>
                    {profileData.ctzn_ph || 'No phone'}
                  </p>
                  {profileData.lst_lgn_ts && (
                    <p className="text-blue-200 text-sm mt-2 flex items-center justify-center md:justify-start gap-1">
                      <i className="ri-time-line"></i>
                      {currentLanguage === 'en' ? 'Last login:' : 'చివరి లాగిన్:'} {profileData.lst_lgn_ts}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all duration-300 border border-white/30"
                >
                  <i className={`ri-${isEditing ? 'save' : 'edit'}-line text-lg`}></i>
                  <span className="font-medium">{isEditing ? (currentLanguage === 'en' ? 'Save' : 'సేవ్') : (currentLanguage === 'en' ? 'Edit' : 'మార్చు')}</span>
                </button>
              </div>
            </div>

            <div className="p-6 md:p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl text-sm flex items-center gap-3">
                  <i className="ri-error-warning-line text-xl"></i>
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="group">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-user-line text-orange-500"></i>
                    {currentLanguage === 'en' ? 'Full Name' : 'పేరు'}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.ctzn_nm}
                      onChange={(e) => setFormData({ ...formData, ctzn_nm: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all"
                      placeholder="Enter your name"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl text-gray-800 font-medium border border-gray-100 capitalize">
                      {profileData.ctzn_nm || '-'}
                    </div>
                  )}
                </div>

                <div className="group">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-mail-line text-orange-500"></i>
                    {currentLanguage === 'en' ? 'Email Address' : 'ఇమెయిల్-ఐడి'}
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.eml_tx}
                      onChange={(e) => setFormData({ ...formData, eml_tx: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all"
                      placeholder="Enter your email"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl text-gray-800 font-medium border border-gray-100">
                      {profileData.eml_tx || '-'}
                    </div>
                  )}
                </div>

                <div className="group">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-phone-line text-orange-500"></i>
                    {currentLanguage === 'en' ? 'Phone Number' : 'ఫోన్ నంబర్'}
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.ctzn_ph}
                      onChange={(e) => setFormData({ ...formData, ctzn_ph: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all"
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl text-gray-800 font-medium border border-gray-100">
                      {profileData.ctzn_ph || '-'}
                    </div>
                  )}
                </div>

                <div className="group">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-building-line text-orange-500"></i>
                    {currentLanguage === 'en' ? 'Organisation' : 'సంస్థ పేరు'}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.orgn_nm}
                      onChange={(e) => setFormData({ ...formData, orgn_nm: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all"
                      placeholder="Enter organisation name"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl text-gray-800 font-medium border border-gray-100">
                      {profileData.orgn_nm || '-'}
                    </div>
                  )}
                </div>

                <div className="col-span-1 md:col-span-2 group">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-map-pin-line text-orange-500"></i>
                    {currentLanguage === 'en' ? 'Address' : 'చిరునామా'}
                  </label>
                  {isEditing ? (
                    <textarea
                      value={formData.addr_tx}
                      onChange={(e) => setFormData({ ...formData, addr_tx: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all resize-none"
                      placeholder="Enter your address"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl text-gray-800 font-medium min-h-[80px] border border-gray-100">
                      {profileData.addr_tx || '-'}
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      if (profileData) {
                        setFormData({
                          ctzn_nm: profileData.ctzn_nm || '',
                          ctzn_ph: profileData.ctzn_ph || '',
                          eml_tx: profileData.eml_tx || '',
                          addr_tx: profileData.addr_tx || '',
                          orgn_nm: profileData.orgn_nm || ''
                        });
                      }
                    }}
                    className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium flex items-center gap-2"
                  >
                    <i className="ri-close-line"></i>
                    {currentLanguage === 'en' ? 'Cancel' : 'రద్దు చేయండి'}
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl hover:from-orange-600 hover:to-amber-600 transition-all font-medium shadow-lg shadow-orange-500/25 flex items-center gap-2"
                  >
                    <i className="ri-save-line"></i>
                    {currentLanguage === 'en' ? 'Save Changes' : 'మార్పులను సేవ్ చేయండి'}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Complaints Tab Content */}
        {activeTab === 'complaints' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <i className="ri-feedback-line text-orange-500"></i>
                    {currentLanguage === 'en' ? 'My Complaints' : 'నా ఫిర్యాదులు'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentLanguage === 'en' ? 'Track and manage your complaints' : 'మీ ఫిర్యాదులను ట్రాక్ చేయండి'}
                  </p>
                </div>
                <button
                  onClick={handleCreateComplaint}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-semibold transition-all shadow-lg shadow-orange-500/25"
                >
                  <i className="ri-add-line text-lg"></i>
                  {currentLanguage === 'en' ? 'Create eComplaint' : 'eComplaint సృష్టించండి'}
                </button>
              </div>
            </div>

            <div className="p-6">
              {complaintsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading complaints...</p>
                  </div>
                </div>
              ) : complaintsError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-xl flex items-center gap-3">
                  <i className="ri-error-warning-line text-xl"></i>
                  {complaintsError}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">S.NO</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Ticket Id</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Created Date</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Ward</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Support User</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Phone</th>
                          <th className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedComplaints.length > 0 ? (
                          paginatedComplaints.map((complaint, index) => (
                            <tr key={complaint.tckt_id} className="hover:bg-orange-50/50 transition-colors">
                              <td className="px-4 py-4 text-sm text-gray-600">{startIndex + index + 1}</td>
                              <td className="px-4 py-4">
                                <span className="text-sm font-bold text-blue-600">#{complaint.tckt_id}</span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">{complaint.tckt_dt || '-'}</td>
                              <td className="px-4 py-4">
                                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                                  complaint.tckt_sts_nm === 'Unassigned'
                                    ? 'bg-amber-100 text-amber-700'
                                    : complaint.tckt_sts_nm === 'Closed'
                                    ? 'bg-green-100 text-green-700'
                                    : complaint.tckt_sts_nm === 'In Progress'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    complaint.tckt_sts_nm === 'Unassigned' ? 'bg-amber-500' :
                                    complaint.tckt_sts_nm === 'Closed' ? 'bg-green-500' :
                                    complaint.tckt_sts_nm === 'In Progress' ? 'bg-blue-500' : 'bg-gray-500'
                                  }`}></span>
                                  {complaint.tckt_sts_nm || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">{complaint.wrd_nm || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-700 font-medium">{complaint.tckt_ctgry_nm || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">{complaint.support_fst_nm || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">{complaint.support_user_ph || '-'}</td>
                              <td className="px-4 py-4 text-center">
                                <button
                                  onClick={() => handleViewComplaint(complaint)}
                                  className="inline-flex items-center justify-center w-9 h-9 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                  title="View Complaint"
                                >
                                  <i className="ri-eye-line text-lg"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={9} className="px-4 py-16 text-center">
                              <div className="flex flex-col items-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                  <i className="ri-inbox-line text-3xl text-gray-400"></i>
                                </div>
                                <p className="text-gray-500 font-medium">
                                  {currentLanguage === 'en' ? 'No complaints found' : 'ఫిర్యాదులు కనుగొనబడలేదు'}
                                </p>
                                <p className="text-gray-400 text-sm mt-1">
                                  {currentLanguage === 'en' ? 'Create your first complaint to get started' : 'ప్రారంభించడానికి మీ మొదటి ఫిర్యాదు సృష్టించండి'}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                {/* Pagination */}
                {complaints.length > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Showing <span className="font-semibold text-gray-700">{startIndex + 1}</span> to <span className="font-semibold text-gray-700">{Math.min(startIndex + itemsPerPage, complaints.length)}</span> of <span className="font-semibold text-gray-700">{complaints.length}</span> entries
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
                        >
                          <i className="ri-arrow-left-s-line text-gray-600"></i>
                        </button>

                        <div className="flex items-center">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                                currentPage === page
                                  ? 'bg-orange-500 text-white shadow-md'
                                  : 'text-gray-600 hover:bg-white hover:shadow-sm'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
                        >
                          <i className="ri-arrow-right-s-line text-gray-600"></i>
                        </button>
                      </div>

                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="h-9 px-3 bg-gray-100 border-0 rounded-lg text-sm text-gray-600 font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer"
                      >
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
          </motion.div>
        )}

        {/* Service Requests Tab Content */}
        {activeTab === 'requests' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <i className="ri-customer-service-2-line text-blue-500"></i>
                    {currentLanguage === 'en' ? 'My Service Requests' : 'నా సేవా అభ్యర్థనలు'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentLanguage === 'en' ? 'Track and manage your service requests' : 'మీ సేవా అభ్యర్థనలను ట్రాక్ చేయండి'}
                  </p>
                </div>
                <button
                  onClick={handleCreateServiceRequest}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/25"
                >
                  <i className="ri-add-line text-lg"></i>
                  {currentLanguage === 'en' ? 'Create Request' : 'అభ్యర్థన సృష్టించండి'}
                </button>
              </div>
            </div>

            <div className="p-6">
              {serviceRequestsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading service requests...</p>
                  </div>
                </div>
              ) : serviceRequestsError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-xl flex items-center gap-3">
                  <i className="ri-error-warning-line text-xl"></i>
                  {serviceRequestsError}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">S.NO</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Ticket Id</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Created Date</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Ward</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Category</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Support User</th>
                          <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Phone</th>
                          <th className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedServiceRequests.length > 0 ? (
                          paginatedServiceRequests.map((request, index) => (
                            <tr key={request.tckt_id} className="hover:bg-blue-50/50 transition-colors">
                              <td className="px-4 py-4 text-sm text-gray-600">{startIndexRequests + index + 1}</td>
                              <td className="px-4 py-4">
                                <span className="text-sm font-bold text-blue-600">#{request.tckt_id}</span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">{request.tckt_dt || '-'}</td>
                              <td className="px-4 py-4">
                                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                                  request.tckt_sts_nm === 'Unassigned'
                                    ? 'bg-amber-100 text-amber-700'
                                    : request.tckt_sts_nm === 'Closed'
                                    ? 'bg-green-100 text-green-700'
                                    : request.tckt_sts_nm === 'In Progress'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    request.tckt_sts_nm === 'Unassigned' ? 'bg-amber-500' :
                                    request.tckt_sts_nm === 'Closed' ? 'bg-green-500' :
                                    request.tckt_sts_nm === 'In Progress' ? 'bg-blue-500' : 'bg-gray-500'
                                  }`}></span>
                                  {request.tckt_sts_nm || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">{request.wrd_nm || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-700 font-medium">{request.tckt_ctgry_nm || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">{request.support_fst_nm || '-'}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">{request.support_user_ph || '-'}</td>
                              <td className="px-4 py-4 text-center">
                                <button
                                  onClick={() => handleViewComplaint(request)}
                                  className="inline-flex items-center justify-center w-9 h-9 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                  title="View Service Request"
                                >
                                  <i className="ri-eye-line text-lg"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={9} className="px-4 py-16 text-center">
                              <div className="flex flex-col items-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                  <i className="ri-inbox-line text-3xl text-gray-400"></i>
                                </div>
                                <p className="text-gray-500 font-medium">
                                  {currentLanguage === 'en' ? 'No service requests found' : 'సేవా అభ్యర్థనలు కనుగొనబడలేదు'}
                                </p>
                                <p className="text-gray-400 text-sm mt-1">
                                  {currentLanguage === 'en' ? 'Create your first service request to get started' : 'ప్రారంభించడానికి మీ మొదటి సేవా అభ్యర్థన సృష్టించండి'}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                {/* Pagination */}
                {serviceRequests.length > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Showing <span className="font-semibold text-gray-700">{startIndexRequests + 1}</span> to <span className="font-semibold text-gray-700">{Math.min(startIndexRequests + itemsPerPageRequests, serviceRequests.length)}</span> of <span className="font-semibold text-gray-700">{serviceRequests.length}</span> entries
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => setCurrentPageRequests(prev => Math.max(1, prev - 1))}
                          disabled={currentPageRequests === 1}
                          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
                        >
                          <i className="ri-arrow-left-s-line text-gray-600"></i>
                        </button>

                        <div className="flex items-center">
                          {Array.from({ length: totalPagesRequests }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentPageRequests(page)}
                              className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                                currentPageRequests === page
                                  ? 'bg-blue-600 text-white shadow-md'
                                  : 'text-gray-600 hover:bg-white hover:shadow-sm'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => setCurrentPageRequests(prev => Math.min(totalPagesRequests, prev + 1))}
                          disabled={currentPageRequests === totalPagesRequests}
                          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:shadow-none transition-all"
                        >
                          <i className="ri-arrow-right-s-line text-gray-600"></i>
                        </button>
                      </div>

                      <select
                        value={itemsPerPageRequests}
                        onChange={(e) => {
                          setItemsPerPageRequests(Number(e.target.value));
                          setCurrentPageRequests(1);
                        }}
                        className="h-9 px-3 bg-gray-100 border-0 rounded-lg text-sm text-gray-600 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      >
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
          </motion.div>
        )}

        {/* Other tabs content */}
        {activeTab !== 'profile' && activeTab !== 'complaints' && activeTab !== 'requests' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-slate-100 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className={`${tabs.find(t => t.id === activeTab)?.icon || 'ri-file-list-3-line'} text-blue-600`}></i>
                {currentLanguage === 'en'
                  ? tabs.find(t => t.id === activeTab)?.labelEn
                  : tabs.find(t => t.id === activeTab)?.labelTe
                }
              </h3>
            </div>
            <div className="p-8">
              <div className="text-center py-12">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-time-line text-4xl text-blue-500"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">
                  {currentLanguage === 'en' ? 'Coming Soon' : 'త్వరలో వస్తుంది'}
                </h4>
                <p className="text-gray-500 max-w-md mx-auto">
                  {currentLanguage === 'en'
                    ? `The ${tabs.find(t => t.id === activeTab)?.labelEn} section is currently under development. Please check back later.`
                    : `${tabs.find(t => t.id === activeTab)?.labelTe} విభాగం ప్రస్తుతం అభివృద్ధిలో ఉంది. దయచేసి తర్వాత తనిఖీ చేయండి.`
                  }
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <Footer currentLanguage={currentLanguage} />
      
      {/* Complaint Category Drawer */}
      <AnimatePresence>
        {isCategoryDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCategoryDrawerOpen(false)}
              className="fixed inset-0 bg-black/50 z-[10000]"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10000] shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="bg-blue-600 text-white px-5 py-4 flex items-center justify-between shadow">
                <p className="text-sm font-bold uppercase tracking-wide flex-1">
                  {currentLanguage === 'en' ? 'Select Complaint Categorys' : 'ఫిర్యాదు వర్గాన్ని ఎంచుకోండి'}
                </p>
                <button
                  onClick={() => setIsCategoryDrawerOpen(false)}
                  className="text-white hover:text-gray-100 transition p-1"
                  aria-label="Close category drawer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Banner with Cartoon Illustration */}
              <div className="bg-gradient-to-r from-blue-700 to-blue-800 px-5 py-4 flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-blue-800 rounded-lg flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-200 to-orange-300"></div>
                    <div className="relative z-10 text-center">
                      <div className="w-12 h-12 bg-yellow-300 rounded-full mx-auto mb-1 flex items-center justify-center">
                        <div className="w-8 h-8 bg-black rounded-full"></div>
                      </div>
                      <div className="w-10 h-6 bg-red-500 rounded-b-full mx-auto"></div>
                    </div>
                  </div>
                </div>
                <p className="text-white text-sm font-bold flex-1 leading-relaxed">
                  {currentLanguage === 'en'
                    ? 'Please select your problem category from the below list ...'
                    : 'దయచేసి క్రింది జాబితా నుండి మీ సమస్య వర్గాన్ని ఎంచుకోండి ...'}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto bg-white">
                {isLoadingCategories ? (
                  <div className="flex items-center justify-center h-full text-blue-600">
                    <i className="ri-loader-4-line animate-spin text-3xl"></i>
                  </div>
                ) : categoriesError ? (
                  <div className="p-6 text-center text-red-600 text-sm">
                    {categoriesError}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {categories.map((category) => {
                      const isExpanded = expandedCategoryId === category.tckt_ctgry_id;
                      return (
                        <div key={category.tckt_ctgry_id} className="px-4 hover:bg-gray-50 transition-colors">
                          <button
                            onClick={() => handleCategoryToggle(category.tckt_ctgry_id)}
                            className="w-full flex items-center justify-between py-4 focus:outline-none group"
                          >
                            <div className="flex items-center gap-3 text-left flex-1">
                              <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center overflow-hidden shadow-sm group-hover:border-blue-400 transition-colors">
                                {category.icn_tx ? (
                                  <img
                                    src={getCategoryIcon(category.tckt_ctgry_nm, category.icn_tx)}
                                    alt={category.tckt_ctgry_nm}
                                    className="w-10 h-10 object-contain p-1"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <i className="ri-file-list-line text-2xl text-blue-600"></i>
                                )}
                              </div>
                              <span className="text-sm font-semibold text-blue-900 uppercase tracking-wide group-hover:text-blue-600 transition-colors">
                                {category.tckt_ctgry_nm}
                              </span>
                            </div>
                            <i
                              className={`ri-${isExpanded ? 'subtract' : 'add'}-line text-2xl transition-all ${
                                isExpanded ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-500'
                              }`}
                            ></i>
                          </button>

                          <AnimatePresence>
                            {isExpanded && category.tckt_sb_ctgry && category.tckt_sb_ctgry.length > 0 && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="pb-4 pl-4 space-y-2 bg-gray-50"
                              >
                                {category.tckt_sb_ctgry.map((sub: any) => (
                                  <motion.button
                                    key={sub.tckt_sb_ctgry_id}
                                    onClick={() => handleSubCategoryClick(category, sub)}
                                    className="w-full flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all group text-left"
                                    whileHover={{ x: 4 }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <i className="ri-add-line text-xl text-blue-600"></i>
                                    <span className="text-sm text-red-600 group-hover:text-red-700 font-medium">{sub.tckt_sb_ctgry_nm}</span>
                                  </motion.button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Service Request Drawer */}
      <AnimatePresence>
        {isServiceRequestDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsServiceRequestDrawerOpen(false)}
              className="fixed inset-0 bg-black/50 z-[10000]"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10000] shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="bg-blue-600 text-white px-5 py-4 flex items-center justify-between shadow">
                <p className="text-sm font-bold uppercase tracking-wide flex-1">
                  {currentLanguage === 'en' ? 'Create Service Requests' : 'సేవా అభ్యర్థనలను సృష్టించండి'}
                </p>
                <button
                  onClick={() => setIsServiceRequestDrawerOpen(false)}
                  className="text-white hover:text-gray-100 transition p-1"
                  aria-label="Close service request drawer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Instruction Text */}
              <div className="px-5 py-3 bg-white border-b border-gray-200">
                <p className="text-red-600 text-sm font-medium">
                  {currentLanguage === 'en' 
                    ? 'Please Select Your Service Request From Below List' 
                    : 'దయచేసి క్రింది జాబితా నుండి మీ సేవా అభ్యర్థనను ఎంచుకోండి'}
                </p>
              </div>

              {/* Service Request Options */}
              <div className="flex-1 overflow-y-auto bg-white">
                <div className="divide-y divide-gray-200">
                  {/* BULK GARBAGE COLLECTION */}
                  <button
                    onClick={() => handleServiceRequestClick('BULK GARBAGE COLLECTION')}
                    className="w-full px-4 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left"
                  >
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                      <i className="ri-delete-bin-7-line text-3xl text-gray-600"></i>
                    </div>
                    <span className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                      BULK GARBAGE COLLECTION
                    </span>
                  </button>

                  {/* SEPTIC TANK CLEANING */}
                  <button
                    onClick={() => handleServiceRequestClick('SEPTIC TANK CLEANING')}
                    className="w-full px-4 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left"
                  >
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                      <i className="ri-truck-line text-3xl text-yellow-600"></i>
                    </div>
                    <span className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                      SEPTIC TANK CLEANING
                    </span>
                  </button>

                  {/* C & D WASTE COLLECTION */}
                  <button
                    onClick={() => handleServiceRequestClick('C & D WASTE COLLECTION')}
                    className="w-full px-4 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left"
                  >
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                      <i className="ri-truck-fill text-3xl text-red-600"></i>
                    </div>
                    <span className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                      C & D WASTE COLLECTION
                    </span>
                  </button>

                  {/* RTI REQUEST */}
                  <button
                    onClick={() => handleServiceRequestClick('RTI REQUEST')}
                    className="w-full px-4 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left"
                  >
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                      <i className="ri-file-text-line text-3xl text-yellow-600"></i>
                    </div>
                    <span className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                      RTI REQUEST
                    </span>
                  </button>

                  {/* REGISTER AS STREET VENDOR */}
                  <button
                    onClick={() => handleServiceRequestClick('REGISTER AS STREET VENDOR')}
                    className="w-full px-4 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left"
                  >
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                      <i className="ri-store-3-line text-3xl text-blue-600"></i>
                    </div>
                    <span className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                      REGISTER AS STREET VENDOR
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bulk Garbage Collection Form Drawer - Removed, using centralized drawer via DrawerManager */}

      {/* Payment Screen Drawer */}
      <AnimatePresence>
        {showPaymentScreen && paymentDetails && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPaymentScreen(false)}
              className="fixed inset-0 bg-black/50 z-[10000]"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10000] shadow-2xl flex flex-col overflow-y-auto"
            >
              {/* Header */}
              <div className="bg-blue-600 text-white px-5 py-4 flex items-center justify-between shadow sticky top-0 z-10">
                <p className="text-sm font-bold uppercase tracking-wide flex-1">
                  BULK GARBAGE COLLECTION
                </p>
                <button
                  onClick={() => setShowPaymentScreen(false)}
                  className="text-white hover:text-gray-100 transition p-1"
                  aria-label="Close payment screen"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Back Button */}
              <div className="px-5 py-3 border-b border-gray-200">
                <button
                  onClick={() => setShowPaymentScreen(false)}
                  className="text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                  <span className="text-sm font-medium">Back</span>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-5 space-y-4">
                {/* Ticket Information */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-red-600">Ticket Id:</span>
                    <span className="text-sm text-gray-900">{submittedTicketId || paymentDetails.tckt_id || 'N/A'}</span>
                  </div>
                  {paymentDetails.asgn_usr_nm && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-red-600">Assigned To:</span>
                      <span className="text-sm text-gray-900">{paymentDetails.asgn_usr_nm}</span>
                    </div>
                  )}
                </div>

                {/* Payment Instructions */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    {currentLanguage === 'en' 
                      ? 'Please scan qr code to complete payment using any UPI enabled payment apps (GPay, PhonePay, Paytm, Amazonpay, etc,..)' 
                      : 'దయచేసి ఏదైనా UPI ప్రారంభించబడిన చెల్లింపు యాప్‌లను (GPay, PhonePay, Paytm, Amazonpay, మొదలైనవి) ఉపయోగించి చెల్లింపును పూర్తి చేయడానికి qr కోడ్‌ను స్కాన్ చేయండి'}
                  </p>
                </div>

                {/* QR Code */}
                {paymentDetails.qr_code && (
                  <div className="bg-white border border-gray-300 rounded-2xl p-4 flex items-center justify-center">
                    <div className="w-full max-w-xs">
                      <img 
                        src={paymentDetails.qr_code} 
                        alt="QR Code" 
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}

                {/* Transaction Details Table */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Transaction No</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.mrcht_trnsn_ref_nu || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">cPay No</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.cpay_trnsn_ref_nu || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Receipt No</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.invce_no || 'NA'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Amount</td>
                        <td className="px-3 py-2 text-sm text-gray-900">Rs.{paymentDetails.amount || '0.00'}/-</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Date&Time</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.trnsn_intlz_ts || 'N/A'}</td>
                      </tr>
                      <tr className="bg-purple-600">
                        <td className="px-3 py-2 text-sm font-semibold text-white">Status</td>
                        <td className="px-3 py-2 text-sm text-white">
                          {paymentDetails.trns_sts || (currentLanguage === 'en' 
                            ? 'Transaction details not found. Please wait and try again.' 
                            : 'లావాదేవీ వివరాలు కనుగొనబడలేదు. దయచేసి వేచి ఉండి మళ్లీ ప్రయత్నించండి.')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Verify Payment Button */}
                <button
                  onClick={handleVerifyPayment}
                  disabled={isVerifyingPayment}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifyingPayment ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      {currentLanguage === 'en' ? 'Verifying...' : 'ధృవీకరిస్తోంది...'}
                    </span>
                  ) : (
                    'VERIFY PAYMENT'
                  )}
                </button>

                {/* Receipt Download Link */}
                {paymentDetails.rcpt_url && (
                  <div className="text-center">
                    <a 
                      href={paymentDetails.rcpt_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline text-sm"
                    >
                      {currentLanguage === 'en' 
                        ? 'Please click here to download "Receipt".' 
                        : 'దయచేసి "రసీదు"ను డౌన్‌లోడ్ చేయడానికి ఇక్కడ క్లిక్ చేయండి.'}
                    </a>
                  </div>
                )}

                {/* Instruction Text */}
                <p className="text-xs text-gray-600 text-center mt-2">
                  {currentLanguage === 'en' 
                    ? "Click on the button 'Verify Payment' for successful or failing payments." 
                    : "విజయవంతమైన లేదా విఫలమైన చెల్లింపుల కోసం 'చెల్లింపును ధృవీకరించండి' బటన్‌పై క్లిక్ చేయండి."}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Chat Drawer - Now handled by centralized DrawerManager */}

      {/* Ticket Details Drawer */}
      <AnimatePresence>
        {isTicketDrawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseTicketDrawer}
              className="fixed inset-0 bg-black bg-opacity-50 z-[10000]"
            />
            
            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-[10000] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-blue-600 text-white px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-white font-bold text-xl">
                  {ticketDetails?.tckt_ctgry_nm || 'Ticket Details'}
                </h2>
                <button
                  onClick={handleCloseTicketDrawer}
                  className="text-white hover:text-gray-100 transition p-1"
                  aria-label="Close"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {isLoadingTicketDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading ticket details...</p>
                    </div>
                  </div>
                ) : ticketDetailsError ? (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    {ticketDetailsError}
                  </div>
                ) : ticketDetails ? (
                  <>
                    {/* Ticket Details Section */}
                    <div className="space-y-2">
                      <div className="flex">
                        <span className="font-semibold text-gray-700 w-32">TicketId:</span>
                        <span className="text-gray-900">#{ticketDetails.tckt_id}</span>
                      </div>
                      <div className="flex bg-gray-100 px-3 py-2 rounded">
                        <span className="font-semibold text-gray-700 w-32">Created Date:</span>
                        <span className="text-gray-900">{ticketDetails.tckt_dt || '-'}</span>
                      </div>
                      <div className="flex bg-gray-100 px-3 py-2 rounded">
                        <span className="font-semibold text-gray-700 w-32">Ward:</span>
                        <span className="text-gray-900">
                          {ticketDetails.wrd_nu 
                            ? `W${ticketDetails.wrd_nu}` 
                            : ticketDetails.wrd_nm 
                              ? ticketDetails.wrd_nm 
                              : '-'}
                        </span>
                      </div>
                      <div className="flex bg-gray-100 px-3 py-2 rounded">
                        <span className="font-semibold text-gray-700 w-32">Sachivalayam:</span>
                        <span className="text-gray-900">{ticketDetails.svm_nm || '-'}</span>
                      </div>
                      {/* <div className="flex">
                        <span className="font-semibold text-gray-700 w-32">Sub-Category:</span>
                        <span className="text-gray-900">{ticketDetails.tckt_sb_ctgry_nm || '-'}</span>
                      </div> */}
                      <div className="flex bg-gray-100 px-3 py-2 rounded">
                        <span className="font-semibold text-gray-700 w-32">Status:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          ticketDetails.tckt_sts_nm === 'Closed'
                            ? 'bg-green-100 text-green-800'
                            : ticketDetails.tckt_sts_nm === 'Assigned'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {ticketDetails.tckt_sts_nm || '-'}
                        </span>
                      </div>
                      <div className="flex">
                        <span className="font-semibold text-gray-700 w-32">Address:</span>
                        <span className="text-gray-900">{ticketDetails.addr_tx || '-'}</span>
                      </div>
                    </div>

                    {/* Problem Description */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Problem description</h3>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                        {ticketDetails.lng_dscn_tx || '-'}
                      </p>
                    </div>

                    {/* Photos/Documents Uploaded */}
                    {ticketDetails.attachments && ticketDetails.attachments.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Photos/Documents Uploaded</h3>
                        <div className="flex gap-4 flex-wrap">
                          {ticketDetails.attachments.map((attachment, index) => (
                            <div key={index} className="relative">
                              {attachment.atcht_url_tx ? (
                                <img
                                  src={attachment.atcht_url_tx}
                                  alt={attachment.fl_name || `Attachment ${index + 1}`}
                                  className="w-24 h-24 object-cover rounded-lg border-2 border-green-500"
                                />
                              ) : (
                                <div className="w-24 h-24 bg-gray-200 rounded-lg border-2 border-gray-300"></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Upload Document */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Upload Document</h3>
                      <label className="block">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors bg-gray-50">
                          {newDocumentPreview ? (
                            <img
                              src={newDocumentPreview}
                              alt="Preview"
                              className="w-full h-48 object-cover rounded-lg mx-auto"
                            />
                          ) : (
                            <div className="flex flex-col items-center">
                              <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-2"></i>
                              <p className="text-gray-600 text-sm">Click to upload document</p>
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx"
                          onChange={handleDocumentChange}
                          className="hidden"
                        />
                      </label>
                    </div>

                    {/* Add Comment */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Add Comment<span className="text-red-600">*</span>
                      </h3>
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Write Your Update Here"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                        rows={4}
                      />
                    </div>

                    {/* UPDATE Button */}
                    <div className="flex justify-center">
                      <motion.button
                        onClick={handleUpdateTicket}
                        disabled={isUpdatingTicket || (!newComment.trim() && !newDocument)}
                        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        whileHover={{ scale: isUpdatingTicket ? 1 : 1.02 }}
                        whileTap={{ scale: isUpdatingTicket ? 1 : 0.98 }}
                      >
                        {isUpdatingTicket ? (
                          <span className="flex items-center gap-2">
                            <i className="ri-loader-4-line animate-spin"></i>
                            Updating...
                          </span>
                        ) : (
                          'UPDATE'
                        )}
                      </motion.button>
                    </div>

                    {/* Previous Comments */}
                    {ticketDetails.comments && ticketDetails.comments.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Previous Comments</h3>
                        <div className="space-y-3">
                          {ticketDetails.comments.map((comment, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <p className="text-gray-900 mb-2">
                                {index + 1}) {comment.cmnt_tx}
                              </p>
                              <div className="flex items-center justify-between text-sm text-gray-600">
                                <span>{comment.cmnt_dt}</span>
                                <span>{comment.create_user_nm || 'Rmc Admin'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Centralized Drawer Manager */}
      <DrawerManager currentLanguage={currentLanguage} />
    </div>
  );
}
